import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { WORK_TYPE_LABELS, type Room, type WizardInput, type WorkType } from "@/types/mvpEstimate";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/posthog";
import {
  computeScopeIdsFromSelectedItems,
  normalizeSelectionToPackages,
  wizardFamiliesForSection,
  wizardSectionsFilteredForWorkType,
  type WizardScopeOption,
} from "@/wizard/mvpWizardScopeCatalog";
import { Info, Trash2 } from "lucide-react";

/** Trzy kroki: typ prac → metraż → zakres (+ opcjonalna notatka). Standard i strefa z preferencji firmy. */
const STEPS = 3;

/** Krótka forma słowa „zakres” przy liczbie (1 zakres, 2–4 zakresy, 5+ zakresów, wyjątek 11–14). */
function polishZakresWord(n: number): string {
  if (n === 1) return "zakres";
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "zakresów";
  if (mod10 >= 2 && mod10 <= 4) return "zakresy";
  return "zakresów";
}

const WORK_TYPE_ORDER: WorkType[] = [
  "malowanie",
  "gladzie",
  "tynki",
  "elewacja",
  "wylewki",
  "plytki",
  "podlogi",
  "zabudowy_gk",
  "bialy_montaz",
];

/** Tylko UI kroku 0 — nie zmienia wartości `WorkType`. */
const WORK_TYPE_TILE_EMOJI: Record<WorkType, string> = {
  malowanie: "🖌️",
  gladzie: "🪟",
  tynki: "🧱",
  elewacja: "🏠",
  wylewki: "🪣",
  plytki: "🔲",
  podlogi: "🥾",
  zabudowy_gk: "📐",
  bialy_montaz: "🚿",
};

/** Jedna opcja zakresu — domyślnie jedna linia; pełny opis po kliknięciu ⓘ. */
function WizardScopeOptionRow({
  opt,
  checked,
  onTogglePackage,
}: {
  opt: WizardScopeOption;
  checked: boolean;
  onTogglePackage: () => void;
}) {
  const [helperOpen, setHelperOpen] = useState(false);

  useEffect(() => {
    if (checked) setHelperOpen(false);
  }, [checked]);

  return (
    <li>
      <div
        className={cn(
          "flex items-center gap-1 rounded-xl border-2 px-2 py-1 transition-colors sm:gap-2 sm:px-3 sm:py-1.5",
          checked ? "shadow-sm" : "",
        )}
        style={{
          borderColor: "var(--color-accent-primary)",
          background: checked ? "var(--color-bg-elevated)" : "var(--color-bg-surface)",
        }}
      >
        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 py-0.5">
          <input
            type="checkbox"
            checked={checked}
            onChange={onTogglePackage}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-2"
            style={{ borderColor: "var(--color-accent-primary)", accentColor: "var(--color-accent-primary)" }}
          />
          <span className="text-sm font-medium leading-snug" style={{ color: "var(--color-text-primary)" }}>
            {opt.label}
          </span>
        </label>
        {opt.helper ? (
          <button
            type="button"
            className={cn(
              "shrink-0 rounded-md border p-1.5 transition-opacity hover:opacity-90",
              helperOpen ? "opacity-95" : "",
            )}
            style={{
              borderColor: "var(--color-accent-secondary)",
              color: "var(--color-accent-primary)",
              background: helperOpen
                ? "color-mix(in srgb, var(--color-accent-secondary) 20%, transparent)"
                : "transparent",
            }}
            aria-expanded={helperOpen}
            aria-label={helperOpen ? "Ukryj opis zakresu" : "Pokaż opis zakresu"}
            onClick={(e) => {
              e.preventDefault();
              setHelperOpen((v) => !v);
            }}
          >
            <Info className="h-4 w-4" strokeWidth={2} aria-hidden />
          </button>
        ) : null}
      </div>
      {helperOpen && opt.helper ? (
        <div
          className="ml-6 mt-2 space-y-2 border-l-2 pl-3 sm:ml-7"
          style={{ borderLeftColor: "color-mix(in srgb, var(--color-accent-primary) 35%, transparent)" }}
        >
          <p className="whitespace-pre-line text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            {opt.helper}
          </p>
          {opt.detailHint ? (
            <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              {opt.detailHint}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export type WizardNavActions = {
  canGoBack: boolean;
  canGoNext: boolean;
  primaryDisabled: boolean;
  onBack: () => void;
  onPrimary: () => void;
  primaryLabel: string;
  stepTitle: string;
};

type Props = {
  wizard: WizardInput;
  onChange: (next: WizardInput) => void;
  onComplete: () => void;
  /** Synchronizacja przycisków w topbarze strony kosztorysu. */
  onNavActionsChange?: (actions: WizardNavActions) => void;
  /** Ukryj dolny pasek Wstecz/Dalej — sterowanie z topbara. */
  hideFooterNav?: boolean;
};

const WIZARD_STEP_TITLES = ["Typ prac", "Metraż", "Zakres prac"] as const;

export function MvpWizard({
  wizard,
  onChange,
  onComplete,
  onNavActionsChange,
  hideFooterNav = false,
}: Props) {
  const step = wizard.step;
  const [step1NextAttempted, setStep1NextAttempted] = useState(false);

  const set = (patch: Partial<WizardInput>) => onChange({ ...wizard, ...patch });

  const rooms = wizard.rooms ?? [];

  const togglePackage = (packageId: string, roomId: string) => {
    const nextRooms = rooms.map((r) => {
      if (r.id !== roomId) return r;
      const next = new Set(r.selectedScopeItemIds);
      if (next.has(packageId)) next.delete(packageId);
      else next.add(packageId);
      const selectedScopeItemIds = normalizeSelectionToPackages([...next]);
      const scopeIds = computeScopeIdsFromSelectedItems(selectedScopeItemIds);
      return { ...r, selectedScopeItemIds, scopeIds };
    });
    set({ rooms: nextRooms });
  };

  const toggleWorkType = (id: WorkType) => {
    const cur = new Set(wizard.workType);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    set({ workType: WORK_TYPE_ORDER.filter((w) => cur.has(w)) });
  };

  const updateRoom = (id: string, patch: Partial<Pick<Room, "name" | "areaM2" | "wallHeight">>) => {
    set({
      rooms: rooms.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };
  const addRoom = () => {
    set({
      rooms: [
        ...rooms,
        {
          id: crypto.randomUUID(),
          name: "",
          areaM2: 0,
          wallHeight: 2.6,
          selectedScopeItemIds: [],
          scopeIds: [],
        },
      ],
    });
  };
  const removeRoom = (id: string) => {
    if (rooms.length <= 1) return;
    set({ rooms: rooms.filter((r) => r.id !== id) });
  };

  const roomsStepValid =
    rooms.length > 0 && rooms.every((r) => r.areaM2 > 0);

  const canNext =
    step === 0
      ? wizard.workType.length >= 1
      : step === 1
        ? roomsStepValid
        : step === 2
          ? wizard.rooms.some((r) => r.scopeIds.length > 0)
          : true;

  const handleDalej = () => {
    if (step === 0) {
      if (wizard.workType.length < 1) return;
      trackEvent("quote_step_completed", { step: 1 });
      set({ step: 1 });
      return;
    }
    if (step === 1) {
      if (!roomsStepValid) {
        setStep1NextAttempted(true);
        return;
      }
      setStep1NextAttempted(false);
      trackEvent("quote_step_completed", { step: 2 });
      set({ step: 2 });
    }
  };

  const handleGoBack = () => {
    setStep1NextAttempted(false);
    set({ step: Math.max(0, step - 1) });
  };

  const handlePrimary = () => {
    if (step < STEPS - 1) {
      handleDalej();
      return;
    }
    if (!canNext) return;
    trackEvent("quote_step_completed", { step: 3 });
    onComplete();
  };

  const primaryLabel = step < STEPS - 1 ? "Dalej" : "Generuj kosztorys";
  const primaryDisabled = step === 0 ? !canNext : step === 1 ? false : step === 2 ? !canNext : false;

  const navActions = useMemo<WizardNavActions>(
    () => ({
      canGoBack: step > 0,
      canGoNext: canNext,
      primaryDisabled,
      onBack: handleGoBack,
      onPrimary: handlePrimary,
      primaryLabel,
      stepTitle: WIZARD_STEP_TITLES[step] ?? "Kreator",
    }),
    [step, canNext, primaryDisabled, primaryLabel]
  );

  useEffect(() => {
    onNavActionsChange?.(navActions);
  }, [navActions, onNavActionsChange]);

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col gap-6">
      <p className="text-xs" style={{ color: "var(--ink-3)" }}>
        Ustawienia <span className="font-medium" style={{ color: "var(--ink)" }}>standardu wykończenia</span> i{" "}
        <span className="font-medium" style={{ color: "var(--ink)" }}>strefy cenowej</span> ustala się przy pierwszym
        uruchomieniu (onboarding) — tu wybierasz typ prac i dane pod ten szacunek.
      </p>

      <div className="flex gap-1.5">
        {Array.from({ length: STEPS }, (_, i) => (
          <div
            key={i}
            className="h-1 flex-1 rounded-full transition-colors"
            style={{
              background: i <= step ? "var(--ochre)" : "var(--line)",
            }}
          />
        ))}
      </div>

      {step === 0 && (
        <section className="flex min-h-0 flex-1 flex-col gap-4">
          <div>
            <h2 className="text-foreground">Typ prac</h2>
            <p className="text-sm text-muted-foreground">
              Lista zakresów w kolejnym kroku dostosuje się do wyboru (np. samo malowanie — tylko pakiety
              malarskie).
            </p>
          </div>
          <div title="Krótki opis projektu (opcjonalnie)">
            <Label htmlFor="mvp-project-description" className="text-sm font-medium text-foreground">
              Opis projektu <span className="font-normal text-muted-foreground">(opcjonalnie)</span>
            </Label>
            <Input
              id="mvp-project-description"
              type="text"
              value={wizard.projectDescription ?? ""}
              onChange={(e) => set({ projectDescription: e.target.value })}
              placeholder="Remont – ul. Lipowa 12"
              maxLength={200}
              className="mt-1.5 h-11 text-sm"
              autoComplete="off"
            />
          </div>
          <div className="grid flex-1 auto-rows-fr content-stretch gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {WORK_TYPE_ORDER.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => toggleWorkType(id)}
                className={cn(
                  "h-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors",
                  !wizard.workType.includes(id) && "hover:bg-[var(--color-bg-elevated)]"
                )}
                style={
                  wizard.workType.includes(id)
                    ? {
                        borderColor: "var(--color-accent-primary)",
                        background: "var(--color-bg-elevated)",
                        color: "var(--color-text-primary)",
                      }
                    : {
                        borderColor: "color-mix(in srgb, var(--color-border-default) 70%, transparent)",
                        background: "var(--color-bg-surface)",
                        color: "var(--color-text-primary)",
                      }
                }
              >
                {WORK_TYPE_TILE_EMOJI[id]} {WORK_TYPE_LABELS[id]}
              </button>
            ))}
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4">
          <div>
            <h2 className="text-foreground">Metraż</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Pomieszczenia — suma m² i wysokości pod heurystykę ścian, sufitu i podłogi.
            </p>
          </div>

          <div className="space-y-4">
            {rooms.map((r, idx) => (
              <div
                key={r.id}
                className="rounded-xl border border-border/70 p-3 shadow-sm sm:p-4"
                style={{ background: "var(--color-bg-surface)" }}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Pomieszczenie {idx + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={rooms.length === 1}
                    onClick={() => removeRoom(r.id)}
                    aria-label="Usuń pomieszczenie"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="sm:col-span-2 lg:col-span-1">
                    <Label htmlFor={`mvp-room-name-${r.id}`} className="text-xs">
                      Nazwa
                    </Label>
                    <Input
                      id={`mvp-room-name-${r.id}`}
                      type="text"
                      value={r.name}
                      onChange={(e) => updateRoom(r.id, { name: e.target.value })}
                      placeholder="np. Salon"
                      className="mt-1.5 h-10 text-sm"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`mvp-room-area-${r.id}`} className="text-xs">
                      Powierzchnia (m²)
                    </Label>
                    <Input
                      id={`mvp-room-area-${r.id}`}
                      type="number"
                      min={0}
                      step={0.01}
                      value={r.areaM2 === 0 ? "" : r.areaM2}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          updateRoom(r.id, { areaM2: 0 });
                          return;
                        }
                        const n = Number(raw);
                        updateRoom(r.id, {
                          areaM2: Number.isFinite(n) ? Math.max(0, n) : 0,
                        });
                      }}
                      className={cn(
                        "mt-1.5 h-10 font-mono text-sm",
                        step1NextAttempted && r.areaM2 <= 0 && "border-destructive"
                      )}
                    />
                    {step1NextAttempted && r.areaM2 <= 0 ? (
                      <p className="mt-1 text-xs text-destructive">Podaj metraż większy niż 0.</p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor={`mvp-room-height-${r.id}`} className="text-xs">
                      Wysokość (m)
                    </Label>
                    <Input
                      id={`mvp-room-height-${r.id}`}
                      type="number"
                      min={0.1}
                      step={0.05}
                      value={r.wallHeight}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const n = Number(raw);
                        updateRoom(r.id, {
                          wallHeight: Number.isFinite(n) && n > 0 ? n : 2.6,
                        });
                      }}
                      className="mt-1.5 h-10 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" onClick={addRoom}>
            + Dodaj pomieszczenie
          </Button>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-foreground">Zakres prac</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">
              Zaznacz paczki robót — od tego zbudujemy pierwszy szkic w tabeli.
            </p>
          </div>

          <Accordion
            key={rooms.map((r) => r.id).join("|")}
            type="single"
            collapsible
            defaultValue={rooms[0]?.id}
            className="space-y-2"
          >
            {rooms.map((room, roomIdx) => {
              const roomTitle = room.name.trim() || `Pomieszczenie ${roomIdx + 1}`;
              const nScopes = room.scopeIds.length;
              const zakresWord = polishZakresWord(nScopes);
              return (
                <AccordionItem
                  key={room.id}
                  value={room.id}
                  className="overflow-hidden rounded-xl border shadow-sm"
                  style={{
                    borderColor: "var(--color-border-default)",
                    background: "var(--color-bg-surface)",
                  }}
                >
                  <AccordionTrigger
                    className={cn(
                      "group w-full flex-wrap items-center gap-x-2 gap-y-1 py-3.5 pl-3 pr-2 text-left hover:no-underline sm:flex-nowrap sm:gap-3 sm:pl-4 sm:pr-3",
                      "data-[state=open]:border-b data-[state=open]:border-[color:color-mix(in_srgb,var(--color-warning)_45%,transparent)] data-[state=open]:bg-[var(--color-bg-elevated)]",
                      "data-[state=closed]:bg-[var(--color-bg-surface)] data-[state=closed]:hover:bg-[var(--color-bg-elevated)]",
                      "data-[state=open]:[&>svg]:text-[var(--color-text-primary)] data-[state=closed]:[&>svg]:text-[var(--color-text-muted)]",
                      "data-[state=open]:[&_span.sb-room-title]:text-[var(--color-text-primary)]",
                      "data-[state=closed]:[&_span.sb-room-title]:text-[var(--color-text-secondary)]",
                      "data-[state=open]:[&_span.sb-room-sep]:text-[color:color-mix(in_srgb,var(--color-text-primary)_45%,transparent)]",
                      "data-[state=closed]:[&_span.sb-room-sep]:text-[color:color-mix(in_srgb,var(--color-text-secondary)_35%,transparent)]",
                      "data-[state=open]:[&_span.sb-room-meta]:text-[var(--color-text-secondary)]",
                      "data-[state=closed]:[&_span.sb-room-meta]:text-[var(--color-text-muted)]",
                    )}
                  >
                    <span className="sb-room-title min-w-0 shrink text-base font-semibold leading-snug">
                      {roomTitle}
                    </span>
                    <span className="sb-room-sep shrink-0" aria-hidden>
                      |
                    </span>
                    <span className="sb-room-meta text-sm">
                      {room.areaM2} m² · {nScopes} {zakresWord}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div
                      className="space-y-8 border-t px-3 pb-4 pt-3 sm:px-4"
                      style={{
                        borderColor: "color-mix(in srgb, var(--color-warning) 20%, transparent)",
                        background: "var(--color-bg-elevated)",
                      }}
                    >
                      {wizard.workType.map((wt) => (
                        <div key={`${room.id}-${wt}`} className="space-y-4">
                          <h3
                            className="border-b pb-2 text-base font-semibold tracking-tight"
                            style={{
                              borderColor: "color-mix(in srgb, var(--color-border-default) 60%, transparent)",
                              color: "var(--color-text-primary)",
                            }}
                          >
                            {WORK_TYPE_LABELS[wt]}
                          </h3>
                          {wizardSectionsFilteredForWorkType(wt).map((section) => {
                            const families = wizardFamiliesForSection(section);
                            if (families.length === 0) return null;
                            return (
                              <div
                                key={`${room.id}-${wt}-${section.id}`}
                                className="rounded-2xl border p-3 shadow-sm sm:p-4"
                                style={{
                                  background: "var(--color-bg-surface)",
                                  borderColor: "color-mix(in srgb, var(--color-border-default) 80%, transparent)",
                                }}
                              >
                                <h4
                                  className="mb-3 rounded-md px-2.5 py-2 text-xs font-semibold uppercase tracking-wider"
                                  style={{
                                    background: "color-mix(in srgb, var(--color-accent-secondary) 13%, transparent)",
                                    color: "var(--color-text-primary)",
                                  }}
                                >
                                  {section.title}
                                </h4>
                                <div className="space-y-4">
                                  {families.map((family) => (
                                    <div
                                      key={`${room.id}-${family.id}`}
                                      className="rounded-xl border px-3 py-3 sm:px-4 sm:py-3.5"
                                      style={{
                                        borderColor: "color-mix(in srgb, var(--color-border-default) 70%, transparent)",
                                        background: "color-mix(in srgb, var(--color-bg-elevated) 80%, transparent)",
                                      }}
                                    >
                                      <div className="mb-2 flex items-start gap-1.5">
                                        <h5
                                          className="text-sm font-semibold leading-snug"
                                          style={{ color: "var(--color-text-primary)" }}
                                        >
                                          {family.label}
                                        </h5>
                                        {family.familyDetailHint ? (
                                          <Tooltip delayDuration={200}>
                                            <TooltipTrigger asChild>
                                              <button
                                                type="button"
                                                className="mt-0.5 shrink-0 rounded hover:opacity-90"
                                                style={{ color: "var(--color-accent-primary)" }}
                                                aria-label={`Szczegóły: ${family.label}`}
                                              >
                                                <Info className="h-4 w-4" strokeWidth={2} />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs text-left text-xs leading-snug">
                                              {family.familyDetailHint}
                                            </TooltipContent>
                                          </Tooltip>
                                        ) : null}
                                      </div>
                                      <ul className="space-y-2">
                                        {family.options.map((opt) => {
                                          const checked = room.selectedScopeItemIds.includes(opt.packageId);
                                          return (
                                            <WizardScopeOptionRow
                                              key={opt.packageId}
                                              opt={opt}
                                              checked={checked}
                                              onTogglePackage={() => togglePackage(opt.packageId, room.id)}
                                            />
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <p className="text-sm text-foreground/80">
            W tabeli dopiszesz ilości, materiały i dopłaty — tu tylko włączasz grupy, które mają wylądować w kosztorysie.
          </p>

          <div className="space-y-2 border-t border-border/50 pt-4">
            <Label htmlFor="mvp-project-notes" className="text-sm font-medium text-foreground">
              Notatka (opcjonalnie)
            </Label>
            <Textarea
              id="mvp-project-notes"
              value={wizard.projectNotes ?? ""}
              onChange={(e) => set({ projectNotes: e.target.value })}
              placeholder="Np. ustalenia z klientem, nietypowy układ — nie dodaje linii automatycznie."
              rows={3}
              className="resize-y text-sm"
              style={{ background: "var(--color-bg-input)", color: "var(--color-text-primary)" }}
            />
            <p className="text-sm text-foreground/75">Tylko dla Ciebie przy wycenie — nie jest mapowana na pozycje.</p>
          </div>
        </section>
      )}

      {!hideFooterNav ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4" style={{ borderColor: "var(--line)" }}>
          <Button
            type="button"
            variant="outline"
            disabled={step === 0}
            onClick={handleGoBack}
            className="border-[var(--line)] bg-transparent hover:bg-[var(--bg-soft)]"
          >
            Wstecz
          </Button>
          <Button
            type="button"
            disabled={primaryDisabled}
            onClick={handlePrimary}
            className="workshop-btn-primary font-medium disabled:opacity-50"
          >
            {primaryLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
